import { Alert, ColorKeys } from 'canoe';
import React from 'react';
import ReactDOM from 'react-dom';

import styled, { css } from 'canoe/lib/styled';
import { useEditor } from 'craft/hooks';
import { CanduCraftIndicator } from 'craft/types';
import { getDomNodeFromDocument } from 'craft/utils/viewport';

import { calculateIndicatorPosition } from './calculateIndicatorPosition';
import { getDOMInfo } from './getDomInfo';
import { getErrorText } from './getErrorText';

interface Props {
  indicator: CanduCraftIndicator;
}

const StyledAlert = styled(Alert)`
  ${({ theme }) => css`
    margin-top: ${theme.spaces.xs};
  `}
`;

const IndicatorContainer = styled.div`
  pointer-events: none; // prevents from intercepting pointer events due for components behind
  position: fixed;
  z-index: 1;
`;

const StyledIndicatorTriangle = styled.div<{
  direction: 'horizontal' | 'vertical';
  position: 'start' | 'end';
}>`
  ${({ direction, position }) => css`
    position: absolute;
    background-color: inherit;
    text-align: left;
    //transform: rotate(-60deg) skewX(-30deg) scale(1, 0.866);

    ${position === 'start' &&
    direction === 'horizontal' &&
    css`
      top: -2px;
      left: 0;
      transform: rotate(30deg) skewX(-30deg) scale(1, 0.866);
    `};

    ${position === 'end' &&
    direction === 'horizontal' &&
    css`
      top: -4px;
      right: -3px;
      transform: rotate(-30deg) skewX(-30deg) scale(1, 0.866);
    `};

    ${position === 'start' &&
    direction === 'vertical' &&
    css`
      top: -3px;
      left: 0;
      transform: rotate(0deg) skewX(-30deg) scale(1, 0.866);
    `};

    ${position === 'end' &&
    direction === 'vertical' &&
    css`
      bottom: 0;
      left: -2px;
      transform: rotate(-60deg) skewX(-30deg) scale(1, 0.866);
    `};

    &:before,
    &:after {
      content: '';
      position: absolute;
      background-color: inherit;
    }

    &,
    &:before,
    &:after {
      width: 7px;
      height: 7px;
      border-top-right-radius: 30%;
    }

    &:before {
      transform: rotate(-135deg) skewX(-45deg) scale(1.414, 0.707) translate(0, -50%);
    }

    &:after {
      transform: rotate(135deg) skewY(-45deg) scale(0.707, 1.414) translate(50%);
    }
  `}
`;

const StyledIndicator = styled.div<{
  width: string;
  height: string;
  color: ColorKeys;
}>`
  ${({ theme, width, height, color }) => css`
    width: ${width};
    height: ${height};
    background-color: ${theme.colors[color]};
    display: block;
    opacity: 1;
    transition: background-color 0.1s ease-in;
    position: relative;
  `}
`;

const RenderIndicator: React.FC<any> = ({ style, parentDom, error }) => {
  const { top, left, width, height, color, direction } = style;

  const indicator = (
    <IndicatorContainer style={{ top, left }}>
      <StyledIndicator width={width} height={height} color={color}>
        <StyledIndicatorTriangle direction={direction} position="start" />
        <StyledIndicatorTriangle direction={direction} position="end" />
      </StyledIndicator>
      {error && (
        <StyledAlert intent="danger" variant="dark" scale="small">
          {error}
        </StyledAlert>
      )}
    </IndicatorContainer>
  );

  if (parentDom && parentDom.ownerDocument !== document) {
    return ReactDOM.createPortal(indicator, parentDom.ownerDocument.body);
  }

  return indicator;
};

export const Indicator = ({ indicator }: Props) => {
  const { query } = useEditor();

  const parentDom = getDomNodeFromDocument(
    indicator.placement.parent.id,
    indicator.parentNodeCollectionItemId,
  );
  const nodeId = indicator.placement.currentNode?.id;
  const currentDom =
    nodeId && getDomNodeFromDocument(nodeId, indicator.currentNodeCollectionItemId);

  if (!parentDom || !currentDom) {
    return null;
  }

  const position = calculateIndicatorPosition(
    indicator.placement,
    getDOMInfo(parentDom),
    nodeId ? getDOMInfo(currentDom) : null,
  );
  let color: ColorKeys = 'AquaTealD10';
  if (indicator.error) {
    color = 'ParadiseRed';
  } else if (indicator.currentNodeCollectionItemId) {
    color = 'MangoOrange';
  }
  const style = { ...position, color };

  const draggedNode =
    indicator.draggedElementId && indicator.draggedElementId[0]
      ? query.node(indicator.draggedElementId[0]).get()
      : indicator.newRootNode;

  return (
    <RenderIndicator
      style={style}
      parentDom={parentDom}
      error={getErrorText(indicator, query, draggedNode)}
    />
  );
};
